import { useContext } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { Permission } from '@/types/model';

/**
 * Hook for checking user permissions
 */
export const usePermissions = () => {
  const { user } = useContext(AuthContext);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    
    // Superadmin and admin roles have all permissions
    if (user.roleCode === 'superadmin' || user.roleCode === 'admin') {
      return true;
    }
    
    return user.permissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!user) return false;
    
    // Superadmin and admin roles have all permissions
    if (user.roleCode === 'superadmin' || user.roleCode === 'admin') {
      return true;
    }
    
    return permissions.some(permission => user.permissions.includes(permission));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    if (!user) return false;
    
    // Superadmin and admin roles have all permissions
    if (user.roleCode === 'superadmin' || user.roleCode === 'admin') {
      return true;
    }
    
    return permissions.every(permission => user.permissions.includes(permission));
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (roleCode: string): boolean => {
    if (!user) return false;
    return user.roleCode === roleCode;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roleCodes: string[]): boolean => {
    if (!user) return false;
    return roleCodes.includes(user.roleCode);
  };

  /**
   * Check if user is admin or superadmin
   */
  const isAdmin = (): boolean => {
    if (!user) return false;
    return user.roleCode === 'superadmin' || user.roleCode === 'admin';
  };

  return {
    user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdmin,
    permissions: user?.permissions || [],
    roleCode: user?.roleCode || null,
  };
};